import { useEffect, useRef, useState } from 'react';
import Icon from '../ui/Icon';
import FieldError from '../ui/FieldError';
import { ApiError } from '../../api/client';
import { updatePassword } from '../../api/auth';
import { formatCountdown } from '../../utils/format';
import { PASSWORD_MAX, PASSWORD_MIN, validateConfirmPassword, validateNewPassword } from '../../utils/validation';
import { AlertBox, PasswordInput, pillButton } from './AuthShell';
import useCountdown from './useCountdown';

/**
 * Step 3: choose the new password. The reset session has its own timer; once it runs
 * out (or the server rejects it) the only way forward is "Start again".
 */
export default function NewPasswordStep({ resetToken, sessionExpiresAt, onDone, onStartAgain }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState({ password: false, confirm: false });
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const firstRef = useRef(null);
  const busy = useRef(false);

  const sessionSeconds = useCountdown(sessionExpiresAt);
  const sessionExpired = sessionSeconds === 0;

  useEffect(() => firstRef.current?.focus(), []);

  const passwordError = validateNewPassword(password);
  const confirmError = validateConfirmPassword(password, confirm);
  const valid = !passwordError && !confirmError;

  const submit = async () => {
    if (busy.current || !valid || sessionExpired) return;
    busy.current = true;
    setSaving(true);
    setServerError('');
    try {
      const { message } = await updatePassword(resetToken, password, confirm);
      onDone(message);
    } catch (err) {
      // 400 = validation, same as the current password, or an expired/used session.
      // The response doesn't say which, so show the message with "Start again" alongside.
      setServerError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const showPasswordError = touched.password && password && passwordError;
  const showConfirmError = touched.confirm && confirm && confirmError;
  const startAgain = (
    <button type="button" onClick={onStartAgain} className="font-bold underline underline-offset-2">
      Start again
    </button>
  );

  return (
    <>
      <h1 className="text-2xl font-bold text-on-surface text-center mb-2.5">Update Password</h1>
      <p className="text-[13.5px] leading-relaxed text-on-surface-variant text-center px-1 mb-4">
        Please enter your new password and confirm it below to securely update your account.
      </p>
      <p className={`text-center text-xs font-data-mono mb-5 ${sessionExpired ? 'text-error font-bold' : 'text-on-surface-variant'}`}>
        {sessionExpired ? 'Session expired' : `Session ends in ${formatCountdown(sessionSeconds)}`}
      </p>

      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {sessionExpired ? (
          <AlertBox tone="pending" action={startAgain}>
            Your reset session has expired. Request a new code to continue.
          </AlertBox>
        ) : (
          serverError && <AlertBox action={startAgain}>{serverError}</AlertBox>
        )}

        <div>
          <label htmlFor="new-password" className="block text-sm font-bold text-on-surface mb-1.5">
            New Password <span className="text-error">*</span>
          </label>
          <PasswordInput
            ref={firstRef}
            id="new-password"
            autoComplete="new-password"
            placeholder="Enter new password"
            maxLength={PASSWORD_MAX + 20}
            value={password}
            disabled={saving || sessionExpired}
            invalid={Boolean(showPasswordError)}
            aria-describedby="new-password-hint"
            onChange={(e) => {
              setPassword(e.target.value);
              setServerError('');
            }}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
          />
          {showPasswordError ? (
            <FieldError id="new-password-hint">{passwordError}</FieldError>
          ) : (
            <p
              id="new-password-hint"
              className={`mt-1.5 flex items-center gap-1 text-xs ${password && !passwordError ? 'text-secondary' : 'text-on-surface-variant'}`}
            >
              <Icon name={password && !passwordError ? 'check_circle' : 'info'} size={14} />
              {PASSWORD_MIN} to {PASSWORD_MAX} characters
            </p>
          )}
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-bold text-on-surface mb-1.5">
            Confirm Password <span className="text-error">*</span>
          </label>
          <PasswordInput
            id="confirm-password"
            autoComplete="new-password"
            placeholder="Confirm new password"
            maxLength={PASSWORD_MAX + 20}
            value={confirm}
            disabled={saving || sessionExpired}
            invalid={Boolean(showConfirmError)}
            aria-describedby={showConfirmError ? 'confirm-password-error' : undefined}
            onChange={(e) => {
              setConfirm(e.target.value);
              setServerError('');
              // Mismatch shows as soon as the confirmation is as long as the password
              if (e.target.value.length >= password.length) setTouched((t) => ({ ...t, confirm: true }));
            }}
            onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
          />
          <FieldError id="confirm-password-error">{showConfirmError ? confirmError : undefined}</FieldError>
        </div>

        <div className="pt-1">
          <button type="submit" disabled={!valid || saving || sessionExpired} className={pillButton}>
            {saving ? (
              <>
                <Icon name="progress_activity" size={20} className="animate-spin" />
                Updating…
              </>
            ) : (
              <>
                Update Password
                <Icon name="arrow_forward" size={20} />
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );
}
